import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { format, addDays, subDays, startOfWeek, addWeeks, subWeeks, parseISO, getDay, nextDay, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay, isToday, Locale } from 'date-fns';
import { getDateLocale } from '@/utils/dateLocale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, CalendarDays, Plus, Clock, User, CheckCircle, UserCheck, Lock, AlertCircle, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useResponsiveBreakpoints } from '@/hooks/useMediaQuery';
import { useSwipeable } from 'react-swipeable';
import { useAuth } from '@/contexts/auth-context';
import { useLocation } from 'wouter';

import { adminAvailabilityService, type GridProvider, type ProviderDayData, type ProviderBookingBlock, type ProviderAvailabilityBlock, type ProviderOverrideBlock, type ProviderCalendarBlock, type ProviderGroupClassSlotBlock, type GroupClassCancelScope } from '@/services/api/admin-availability.service';
import { staffAvailabilityService, type ProviderAvailability, type AvailabilityOverride } from '@/services/api/availability.service';
import { CreateAvailabilityWizard, type RecurringData, type DateRangeData, type OneTimeData } from '@/components/availability/CreateAvailabilityWizard';
import { EditAvailabilityDialog, type UpdateFutureData, type UpdateGroupData, type CreateMultiDayData } from '@/components/availability/EditAvailabilityDialog';
import { EditOverrideDialog, type UpdateOverrideData } from '@/components/availability/EditOverrideDialog';
import { EditGroupClassSlotDialog } from '@/components/availability/EditGroupClassSlotDialog';
import { waitlistService } from '@/services/api/waitlist.service';
import type { GroupClassSlotDetail } from '@/services/api/admin-availability.service';
import { bookingService } from '@/services/api/booking.service';

import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { FloatingActionButton } from '@/components/ui/floating-action-button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ViewMode } from '@/components/availability/AvailabilityGrid';
import { Calendar as ScheduleCalendar } from '@/components/calendar/Calendar';
import type { CalendarEvent, CalendarLayer, CalendarLayerInterval } from '@/lib/calendar/types';
import { buildAdminCalendarBundle } from '@/lib/calendar/admin';


export default function AdminAvailabilityPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isMobile, isTablet } = useResponsiveBreakpoints();
  const dateLocale = getDateLocale();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'day' : 'week'));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<GridProvider | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<ProviderBookingBlock | null>(null);
  const [bookingSheetOpen, setBookingSheetOpen] = useState(false);

  const staffTabsRef = useRef<HTMLDivElement>(null);

  // Create availability dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogProvider, setCreateDialogProvider] = useState<GridProvider | null>(null);
  const [createDialogTime, setCreateDialogTime] = useState<string | null>(null);
  const [createDialogInitialStep, setCreateDialogInitialStep] = useState<0 | 1 | 2 | 3 | 4 | 5 | undefined>(undefined);
  const [skipWizardReset, setSkipWizardReset] = useState(false);

  // Edit availability dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editAvailability, setEditAvailability] = useState<ProviderAvailability | null>(null);
  const [editProvider, setEditProvider] = useState<GridProvider | null>(null);
  const [editAvailabilityDate, setEditAvailabilityDate] = useState<string | null>(null);

  // Edit override dialog state
  const [editOverrideDialogOpen, setEditOverrideDialogOpen] = useState(false);
  const [editOverride, setEditOverride] = useState<AvailabilityOverride | null>(null);

  // Group-class slot edit dialog. Holds the id while the detail loads, then
  // the detail itself once fetched. Tabs in the dialog let admins move the
  // class, see its participants, or cancel it.
  const [editGroupClassSlotId, setEditGroupClassSlotId] = useState<number | null>(null);
  const [editGroupClassDetail, setEditGroupClassDetail] = useState<GroupClassSlotDetail | null>(null);
  const [loadingGroupClassDetail, setLoadingGroupClassDetail] = useState(false);
  const [savingGroupClassSlot, setSavingGroupClassSlot] = useState(false);
  const [cancellingGroupClassSlot, setCancellingGroupClassSlot] = useState(false);

  // Waitlist data is fetched only when the dialog is open AND the slot's
  // service has waitlist enabled — otherwise we skip the request entirely.
  const waitlistEnabledForOpenSlot = editGroupClassDetail?.waitlist_enabled === true;
  const { data: waitlistEntries = [], isLoading: loadingWaitlist } = useQuery({
    queryKey: ['admin-waitlist-entries', editGroupClassSlotId],
    queryFn: () => waitlistService.adminListBySlot(editGroupClassSlotId!),
    enabled: editGroupClassSlotId !== null && waitlistEnabledForOpenSlot,
  });

  const handleRemoveWaitlistEntry = async (entryId: number) => {
    try {
      await waitlistService.adminRemove(entryId);
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist-entries', editGroupClassSlotId] });
      toast({
        title: t('common:success'),
        description: t('waitlist:admin.removeSuccess', 'Waitlist entry removed.'),
      });
    } catch (err: any) {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('common:errors.generic'),
        variant: 'destructive',
      });
    }
  };
  // Remove-cancelled-class confirm dialog. Holds the slot id so the action
  // knows which slot to hard-delete.
  const [removeCancelledSlotId, setRemoveCancelledSlotId] = useState<number | null>(null);
  const [removingCancelledSlot, setRemovingCancelledSlot] = useState(false);

  useSetPageHeader(
    {
      title: t('admin:availability.title', 'Team Schedule'),
      sub: t('admin:availability.subtitle', "View and manage your team's availability"),
      actions: isMobile ? undefined : (
        <Button size="md" onClick={() => setCreateDialogOpen(true)} iconLeft={<Plus className="h-4 w-4" />}>
          {t('admin:availability.addAvailability', 'Add Availability')}
        </Button>
      ),
    },
    [t, isMobile],
  );

  const handleGroupClassSlotClick = async (
    slot: ProviderGroupClassSlotBlock,
    _provider: GridProvider,
    _date: string,
  ) => {
    // Cancelled slots open a small "Remove from schedule" confirmation
    // instead of the edit dialog (the slot is no longer editable).
    if (slot.cancelled) {
      setRemoveCancelledSlotId(slot.id);
      return;
    }
    setEditGroupClassSlotId(slot.id);
    setEditGroupClassDetail(null);
    setLoadingGroupClassDetail(true);
    try {
      const detail = await adminAvailabilityService.getGroupClassSlot(slot.id);
      setEditGroupClassDetail(detail);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error?.response?.data?.error
          || t('staff:availability.errors.failedToLoad', 'Failed to load class'),
        variant: 'destructive',
      });
      setEditGroupClassSlotId(null);
    } finally {
      setLoadingGroupClassDetail(false);
    }
  };

  const handleConfirmRemoveCancelled = async () => {
    if (!removeCancelledSlotId) return;
    setRemovingCancelledSlot(true);
    try {
      await adminAvailabilityService.removeGroupClassSlot(removeCancelledSlotId);
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      toast({
        title: t('staff:availability.groupClass.toasts.removed', 'Class removed from schedule.'),
      });
      setRemoveCancelledSlotId(null);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error?.response?.data?.error
          || t('common:errors.deleteFailed', { resource: 'class' }),
        variant: 'destructive',
      });
    } finally {
      setRemovingCancelledSlot(false);
    }
  };

  const closeGroupClassDialog = () => {
    setEditGroupClassSlotId(null);
    setEditGroupClassDetail(null);
  };

  const loadGroupClassProviderOptions = (params: { date: string; start_time: string }) => {
    if (!editGroupClassSlotId) return Promise.resolve([]);
    return adminAvailabilityService.getGroupClassProviderOptions(editGroupClassSlotId, params);
  };

  const handleSaveGroupClassSlot = async (params: { date: string; start_time: string; provider_id?: number }) => {
    if (!editGroupClassSlotId) return;
    setSavingGroupClassSlot(true);
    try {
      await adminAvailabilityService.updateGroupClassSlot(
        editGroupClassSlotId,
        params,
      );
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      toast({
        title: t('common:toasts.success'),
        description: t('staff:availability.groupClass.toasts.updated', 'Class updated.'),
      });
      closeGroupClassDialog();
    } catch (error: any) {
      const message = error?.response?.data?.errors?.[0]
        || error?.response?.data?.error
        || t('staff:availability.errors.failedToUpdate', 'Failed to update class');
      toast({
        title: t('common:toasts.error'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSavingGroupClassSlot(false);
    }
  };

  const handleCancelGroupClassSlot = async (scope: GroupClassCancelScope) => {
    if (!editGroupClassSlotId) return;
    setCancellingGroupClassSlot(true);
    try {
      const result = await adminAvailabilityService.cancelGroupClassSlot(editGroupClassSlotId, scope);
      queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
      toast({
        title: result.cancelled_count > 1
          ? t(
              'staff:availability.groupClass.toasts.cancelledMany',
              '{{count}} classes cancelled.',
              { count: result.cancelled_count }
            )
          : t(
              'staff:availability.groupClass.toasts.cancelled',
              'Class cancelled.'
            ),
      });
      closeGroupClassDialog();
    } catch (error: any) {
      const message = error?.response?.data?.errors?.[0]
        || error?.response?.data?.error
        || t('common:errors.deleteFailed', { resource: 'class' });
      toast({
        title: t('common:toasts.error'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setCancellingGroupClassSlot(false);
    }
  };

  // Calendar picker state
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [conflictDialogData, setConflictDialogData] = useState<{
    conflicts: Array<{
      day_of_week: number;
      day_name: string;
      conflicting: Array<{ id: number; start_time: string; end_time: string; recurrence_summary: string; services?: Array<{ id: number; name: string }> }>;
    }>;
    pendingRequest: Parameters<typeof adminAvailabilityService.createRecurringAvailability>[0];
  } | null>(null);

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'day') {
      return {
        startDate: format(selectedDate, 'yyyy-MM-dd'),
        endDate: format(selectedDate, 'yyyy-MM-dd'),
      };
    }
    if (viewMode === 'month') {
      // For month view, fetch the entire month's data
      const monthStart = startOfMonth(selectedDate);
      const monthEnd = endOfMonth(selectedDate);
      return {
        startDate: format(monthStart, 'yyyy-MM-dd'),
        endDate: format(monthEnd, 'yyyy-MM-dd'),
      };
    }
    // Week view
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
    return {
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(addDays(weekStart, 6), 'yyyy-MM-dd'),
    };
  }, [selectedDate, viewMode]);

  // Fetch grid data - only when authenticated
  const { data: gridData, isLoading, error } = useQuery({
    queryKey: ['admin-availability-grid', dateRange.startDate, dateRange.endDate],
    queryFn: () => adminAvailabilityService.getGrid({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }),
    staleTime: 30000, // 30 seconds
    enabled: isAuthenticated && !authLoading,
    // Keep the previous week's grid visible while the next week loads. Avoids
    // a full loading flash on navigation and prevents allProviders from going
    // empty mid-fetch (which would reset the selected provider, see below).
    placeholderData: keepPreviousData,
  });

  // Fetch services for create dialog - only when authenticated
  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
    enabled: isAuthenticated && !authLoading,
  });

  const allProviders = gridData?.providers || [];

  // Desktop: single-provider Calendar view. Default to first provider.
  useEffect(() => {
    if (allProviders.length === 0) return; // grid still loading — keep selection
    if (selectedProviderId == null) {
      setSelectedProviderId(allProviders[0].id);
    } else if (!allProviders.some(p => p.id === selectedProviderId)) {
      setSelectedProviderId(allProviders[0].id);
    }
  }, [allProviders, selectedProviderId]);

  const activeProvider = useMemo(
    () => allProviders.find(p => p.id === selectedProviderId) ?? null,
    [allProviders, selectedProviderId],
  );

  const adminBundle = useMemo(
    () =>
      activeProvider
        ? buildAdminCalendarBundle(activeProvider.days, {
            busyFallbackLabel: t('availability.calendarBusyShort'),
            allServicesLabel: t('availability.allServicesLabel'),
          })
        : null,
    [activeProvider, t],
  );

  const calendarEvents: CalendarEvent[] = adminBundle?.events ?? [];

  const calendarLayers: CalendarLayer[] = useMemo(() => {
    if (!adminBundle || !activeProvider) return [];
    return [
      {
        id: 'availability',
        variant: 'available',
        intervals: adminBundle.availability,
        onIntervalClick: (i: CalendarLayerInterval) => {
          const d = i.data as { kind: string; availability: ProviderAvailabilityBlock; dateStr: string } | undefined;
          if (d) handleAvailabilityClick(d.availability, activeProvider, d.dateStr);
        },
      },
      {
        id: 'override-available',
        variant: 'override-available',
        intervals: adminBundle.overrideAvailable,
        onIntervalClick: (i) => {
          const d = i.data as { override: ProviderOverrideBlock } | undefined;
          if (d) handleOverrideClick(d.override, activeProvider);
        },
      },
      {
        id: 'override-blocked',
        variant: 'override-blocked',
        intervals: adminBundle.overrideBlocked,
        onIntervalClick: (i) => {
          const d = i.data as { override: ProviderOverrideBlock } | undefined;
          if (d) handleOverrideClick(d.override, activeProvider);
        },
      },
      {
        id: 'group-class',
        variant: 'group-class',
        intervals: adminBundle.groupClass,
        onIntervalClick: (i) => {
          const d = i.data as { slot: ProviderGroupClassSlotBlock; dateStr: string } | undefined;
          if (d) handleGroupClassSlotClick(d.slot, activeProvider, d.dateStr);
        },
      },
      {
        id: 'calendar-busy',
        variant: 'busy',
        intervals: adminBundle.busy,
      },
    ];
  }, [adminBundle, activeProvider]);

  // Strip service-name labels on mobile week view — recurring + one-time
  // availability blocks are too narrow to render text without visual noise.
  const displayLayers: CalendarLayer[] = useMemo(() => {
    const stripLabels = isMobile && viewMode === 'week';
    if (!stripLabels) return calendarLayers;
    return calendarLayers.map((layer) =>
      layer.variant === 'available' || layer.variant === 'override-available'
        ? { ...layer, intervals: layer.intervals.map((i) => ({ ...i, label: undefined })) }
        : layer,
    );
  }, [calendarLayers, isMobile, viewMode]);

  const handleCalendarEventClick = (e: CalendarEvent) => {
    if (!activeProvider) return;
    const m = e.meta as { kind?: string; booking?: ProviderBookingBlock } | undefined;
    if (m?.kind === 'booking' && m.booking) {
      handleBookingClick(m.booking, activeProvider);
    }
  };

  const handleCalendarSlotClick = (s: { start: Date; columnId: string }) => {
    // A month cell is a whole day, not a time slot — drill into it instead of
    // opening the availability dialog at midnight.
    if (viewMode === 'month') {
      setSelectedDate(s.start);
      setViewMode('day');
      return;
    }
    if (!activeProvider) return;
    handleCellClick(activeProvider, format(s.start, 'HH:mm'), s.columnId);
  };

  // State to store week availabilities for edit dialog
  const [weekAvailabilitiesForEdit, setWeekAvailabilitiesForEdit] = useState<Array<Partial<ProviderAvailability> & { day_of_week: number; recurrence_group_id: string | null }>>([]);

  // Navigation handlers
  const handlePrevious = () => {
    if (viewMode === 'day') {
      setSelectedDate(subDays(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(subWeeks(selectedDate, 1));
    } else {
      setSelectedDate(subMonths(selectedDate, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(addWeeks(selectedDate, 1));
    } else {
      setSelectedDate(addMonths(selectedDate, 1));
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Cell click handler - opens create dialog
  const handleCellClick = (provider: GridProvider, time: string, date: string) => {
    setCreateDialogProvider(provider);
    setCreateDialogTime(time);
    setSelectedDate(parseISO(date));
    setCreateDialogOpen(true);
  };

  // Booking click handler
  const handleBookingClick = (booking: ProviderBookingBlock, provider: GridProvider) => {
    setSelectedBooking(booking);
    setSelectedProvider(provider);
    setBookingSheetOpen(true);
  };

  // Availability click handler - fetch full data and week data, then open edit dialog
  const handleAvailabilityClick = async (availability: ProviderAvailabilityBlock, provider: GridProvider, clickedDate?: string) => {
    try {
      // Store the clicked date for single-day delete
      setEditAvailabilityDate(clickedDate || format(selectedDate, 'yyyy-MM-dd'));
      // Calculate week range for this date
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');

      // Fetch full availability details AND week grid data in parallel
      const [fullAvailability, weekData] = await Promise.all([
        adminAvailabilityService.getAvailability(availability.id),
        queryClient.fetchQuery({
          queryKey: ['admin-availability-grid-week', weekStartStr, weekEndStr],
          queryFn: () => adminAvailabilityService.getGrid({
            startDate: weekStartStr,
            endDate: weekEndStr,
          }),
          staleTime: 30000,
        }),
      ]);

      // Build week availabilities with day_of_week for the provider
      const providerData = weekData.providers.find(p => p.id === provider.id);
      const weekAvailabilities: Array<Partial<ProviderAvailability> & { day_of_week: number; recurrence_group_id: string | null }> = [];

      if (providerData) {
        Object.entries(providerData.days).forEach(([dateStr, dayData]) => {
          const dayOfWeek = parseISO(dateStr).getDay();
          dayData.availabilities?.forEach(avail => {
            weekAvailabilities.push({
              id: avail.id,
              day_of_week: dayOfWeek,
              recurrence_group_id: avail.recurrence_group_id,
              start_time: avail.start_time,
              end_time: avail.end_time,
              recurrence_type: avail.recurrence_type,
              services: avail.services,
              service_ids: avail.services.map(s => s.id),
            });
          });
        });
      }

      // Set state and open dialog
      setWeekAvailabilitiesForEdit(weekAvailabilities);
      setEditAvailability(fullAvailability);
      setEditProvider(provider);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      toast({
        title: t('common:errors.error'),
        description: t('common:errors.loadingFailed'),
        variant: 'destructive',
      });
    }
  };

  // Override click handler - fetch full data and open edit override dialog
  const handleOverrideClick = async (override: ProviderOverrideBlock, provider: GridProvider) => {
    try {
      const fullOverride = await adminAvailabilityService.getOverride(override.id);
      setEditOverride(fullOverride);
      setEditOverrideDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch override:', error);
      toast({
        title: t('common:errors.error'),
        description: t('common:errors.loadingFailed'),
        variant: 'destructive',
      });
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Scroll active provider tab into view when selection changes
  useEffect(() => {
    if (staffTabsRef.current && allProviders.length) {
      const idx = allProviders.findIndex(p => p.id === selectedProviderId);
      const tabs = staffTabsRef.current.children;
      if (idx >= 0 && tabs[idx]) {
        (tabs[idx] as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }
  }, [selectedProviderId, allProviders]);

  // Swipe handlers for mobile calendar — switch provider
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      const idx = allProviders.findIndex(p => p.id === selectedProviderId);
      if (idx >= 0 && idx < allProviders.length - 1) {
        setSelectedProviderId(allProviders[idx + 1].id);
      }
    },
    onSwipedRight: () => {
      const idx = allProviders.findIndex(p => p.id === selectedProviderId);
      if (idx > 0) {
        setSelectedProviderId(allProviders[idx - 1].id);
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: false,
    delta: 50,
  });

  // Mobile: Swipeable staff calendar view
  if (isMobile) {
    const providers = allProviders;
    const currentProvider = activeProvider;

    return (
      <div className="space-y-4">
        {!providers.length ? (
          <div className="flex-1 flex items-center justify-center py-16 text-soft">
            <div className="text-center">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('admin:availability.noStaff', 'No staff members found')}</p>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'bg-white rounded-lg border border-line overflow-hidden flex flex-col',
              // Day/week scroll inside a viewport-sized card; month grows to its
              // full grid so the page scrolls instead of nesting two scrollers.
              viewMode === 'month' ? 'h-auto' : 'h-[calc(100dvh-7rem)]',
            )}
          >
            {/* 1. View tabs */}
            <div className="border-b border-line p-3 shrink-0">
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <TabsList className="w-full">
                  <TabsTrigger value="day" className="flex-1">{t('admin:availability.day', 'Day')}</TabsTrigger>
                  <TabsTrigger value="week" className="flex-1">{t('admin:availability.week', 'Week')}</TabsTrigger>
                  <TabsTrigger value="month" className="flex-1">{t('admin:availability.month', 'Month')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* 2. Provider strip */}
            <div className="border-b border-line px-2 py-2 overflow-x-auto touch-pan-x shrink-0">
              <div ref={staffTabsRef} className="flex gap-2 min-w-min">
                {providers.map((provider) => {
                  const active = provider.id === selectedProviderId;
                  return (
                    <button
                      key={provider.id}
                      onClick={() => setSelectedProviderId(provider.id)}
                      className={cn(
                        'flex flex-col items-center p-2 rounded-lg min-w-[72px] transition-all',
                        active
                          ? 'bg-white border-2 border-brand-500 shadow-sm'
                          : 'bg-surface-2 border-2 border-transparent',
                      )}
                    >
                      <Avatar className="h-10 w-10 mb-1">
                        <AvatarImage src={provider.avatar_url || undefined} />
                        <AvatarFallback className={cn('text-xs', active ? 'bg-brand-100 text-brand-500' : 'bg-surface-2')}>
                          {getInitials(provider.display_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className={cn('text-xs font-medium truncate max-w-[60px]', active && 'text-brand-500')}>
                        {provider.display_name.split(' ')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : currentProvider ? (
              <div
                // Week view scrolls its columns horizontally, so the
                // swipe-to-switch-provider gesture would fight it.
                {...(viewMode === 'week' ? {} : swipeHandlers)}
                className={cn(
                  'flex flex-col',
                  viewMode === 'week' ? 'touch-auto' : 'touch-pan-y',
                  viewMode === 'month' ? 'shrink-0' : 'flex-1 min-h-0',
                )}
              >
                <ScheduleCalendar
                  view={viewMode}
                  date={selectedDate}
                  onDateChange={setSelectedDate}
                  events={calendarEvents}
                  layers={displayLayers}
                  hideViewTabs
                  bodyFill
                  onEventClick={handleCalendarEventClick}
                  onSlotClick={handleCalendarSlotClick}
                  className={cn(viewMode === 'month' ? 'h-auto shrink-0' : 'flex-1 min-h-0')}
                />
              </div>
            ) : null}
          </div>
        )}

        <FloatingActionButton
          icon={<Plus className="w-5 h-5" />}
          label={t('admin:availability.addAvailability', 'Add Availability')}
          onClick={() => {
            if (currentProvider) {
              setCreateDialogProvider(currentProvider);
            }
            setCreateDialogOpen(true);
          }}
        />

        {/* Create Availability Dialog - Mobile */}
        <CreateAvailabilityWizard
          open={createDialogOpen}
          onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) { setCreateDialogInitialStep(undefined); setSkipWizardReset(false); } }}
          initialStep={createDialogInitialStep}
          skipResetOnClose={skipWizardReset}
          services={services.map(s => ({
            id: s.id,
            name: s.name,
            active: s.active,
            max_capacity: s.max_capacity,
            duration_minutes: s.duration_minutes ?? s.duration,
          }))}
          providers={gridData?.providers?.map(p => ({
            id: p.id,
            name: p.display_name,
            avatar_url: p.avatar_url,
          })) || []}
          selectedProviderId={createDialogProvider?.id}
          onCreateRecurring={async (data) => {
            const params = {
              provider_id: data.provider_id!,
              days_of_week: data.days_of_week,
              start_time: data.start_time,
              end_time: data.end_time,
              recurrence_type: data.recurrence_type,
              recurrence_interval: data.recurrence_interval,
              monthly_week: data.monthly_week,
              monthly_day: data.monthly_day,
              end_type: data.end_type,
              end_date: data.end_date,
              occurrences: data.occurrences,
              service_ids: data.service_ids,
            };
            try {
              await adminAvailabilityService.createRecurringAvailability(params);

              const today = new Date();
              const todayDayOfWeek = getDay(today);
              let firstDate = today;
              if (data.days_of_week.length > 0) {
                const sortedDays = [...data.days_of_week].sort((a, b) => a - b);
                const nextDayIndex = sortedDays.find(d => d >= todayDayOfWeek);
                const targetDay = nextDayIndex !== undefined ? nextDayIndex : sortedDays[0];
                if (targetDay === todayDayOfWeek) {
                  firstDate = today;
                } else if (targetDay > todayDayOfWeek) {
                  firstDate = addDays(today, targetDay - todayDayOfWeek);
                } else {
                  firstDate = addDays(today, 7 - todayDayOfWeek + targetDay);
                }
              }
              setSelectedDate(firstDate);
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              toast({
                title: t('common:toasts.createSuccess'),
                description: t('staff:availability.created', 'Availability created successfully'),
              });
            } catch (error: any) {
              const errorData = error.response?.data;
              if (errorData?.conflict && errorData?.conflicts) {
                setSkipWizardReset(true);
                setCreateDialogOpen(false);
                setConflictDialogData({ conflicts: errorData.conflicts, pendingRequest: params });
                return;
              }
              toast({
                title: t('common:toasts.error'),
                description: t('common:errors.createFailed', { resource: 'availability' }),
                variant: 'destructive',
              });
            }
          }}
          onCreateDateRange={async (data) => {
            try {
              await adminAvailabilityService.createDateRangeAvailability({
                provider_id: data.provider_id!,
                start_date: data.start_date,
                end_date: data.end_date,
                start_time: data.start_time,
                end_time: data.end_time,
                reason: data.reason,
                service_ids: data.service_ids,
              });

              const firstDate = parseISO(data.start_date);
              setSelectedDate(firstDate);
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              toast({
                title: t('common:toasts.createSuccess'),
                description: t('staff:availability.created', 'Availability created successfully'),
              });
            } catch (error) {
              toast({
                title: t('common:toasts.error'),
                description: t('common:errors.createFailed', { resource: 'availability' }),
                variant: 'destructive',
              });
            }
          }}
          onCreateOneTime={async (data) => {
            try {
              await adminAvailabilityService.createOneTimeAvailability({
                provider_id: data.provider_id!,
                date: data.date,
                start_time: data.start_time,
                end_time: data.end_time,
                reason: data.reason,
                service_ids: data.service_ids,
              });

              const firstDate = parseISO(data.date);
              setSelectedDate(firstDate);
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              toast({
                title: t('common:toasts.createSuccess'),
                description: t('staff:availability.created', 'Availability created successfully'),
              });
            } catch (error) {
              toast({
                title: t('common:toasts.error'),
                description: t('common:errors.createFailed', { resource: 'availability' }),
                variant: 'destructive',
              });
            }
          }}
          onCreateGroupRecurring={async (data) => {
            try {
              const svc = services.find(s => s.id === data.service_id);
              const result = await adminAvailabilityService.createGroupClassRecurringAvailability({
                provider_id: data.provider_id!,
                service_id: data.service_id,
                days_of_week: data.days_of_week,
                start_time: data.start_time,
                recurrence_type: data.recurrence_type,
                recurrence_interval: data.recurrence_interval,
                monthly_week: data.monthly_week,
                monthly_day: data.monthly_day,
                end_type: data.end_type,
                end_date: data.end_date,
                occurrences: data.occurrences,
                effective_from: data.effective_from,
              });
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              toast({
                title: t('common:toasts.createSuccess'),
                description: t(
                  'staff:availability.groupClass.toasts.created',
                  'Created {{count}} {{service}} classes ({{capacity}} spots each)',
                  {
                    count: result.slots_created,
                    service: svc?.name ?? 'group',
                    capacity: svc?.max_capacity ?? 0,
                  }
                ),
              });
            } catch (error: any) {
              toast({
                title: t('common:toasts.error'),
                description: error?.response?.data?.error
                  || t('common:errors.createFailed', { resource: 'availability' }),
                variant: 'destructive',
              });
            }
          }}
          manageServicesHref="/admin/services"
          onCreateGroupOneTime={async (data) => {
            try {
              const svc = services.find(s => s.id === data.service_id);
              await adminAvailabilityService.createGroupClassOneTimeAvailability({
                provider_id: data.provider_id!,
                service_id: data.service_id,
                date: data.date,
                start_time: data.start_time,
              });
              setSelectedDate(parseISO(data.date));
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              toast({
                title: t('common:toasts.createSuccess'),
                description: t(
                  'staff:availability.groupClass.toasts.createdOne',
                  'Created 1 {{service}} class ({{capacity}} spots)',
                  {
                    service: svc?.name ?? 'group',
                    capacity: svc?.max_capacity ?? 0,
                  }
                ),
              });
            } catch (error) {
              toast({
                title: t('common:toasts.error'),
                description: t('common:errors.createFailed', { resource: 'availability' }),
                variant: 'destructive',
              });
            }
          }}
        />

        {/* Edit Availability Dialog - Mobile */}
        <EditAvailabilityDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          availability={editAvailability}
          selectedDate={editAvailabilityDate || undefined}
          allAvailabilities={weekAvailabilitiesForEdit as ProviderAvailability[]}
          services={services
            .filter(s => editAvailability?.kind === 'group_class'
              ? (s.max_capacity ?? 1) > 1
              : (s.max_capacity ?? 1) === 1)
            .map(s => ({ id: s.id, name: s.name, active: s.active }))}
          onUpdateFuture={async (id, data) => {
            await adminAvailabilityService.updateFutureAvailability(id, data);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.updated'),
            });
          }}
          onCreateMultiDay={async (data) => {
            await staffAvailabilityService.createMultiDayAvailability(data);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          }}
          onDelete={async (id) => {
            await adminAvailabilityService.deleteAvailability(id);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.deleted'),
            });
          }}
          onDeleteSingleDay={async (date, recurrenceGroupId, startTime, endTime) => {
            if (editProvider) {
              await adminAvailabilityService.markDateUnavailable(editProvider.id, date, undefined, recurrenceGroupId, startTime, endTime);
              queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
              setEditDialogOpen(false);
              toast({
                title: t('common:success'),
                description: t('staff:availability.deleted'),
              });
            }
          }}
          onDeleteGroup={async (groupId, fromDate) => {
            if (fromDate) {
              await adminAvailabilityService.endGroup(groupId, fromDate);
            } else {
              await adminAvailabilityService.deleteGroup(groupId);
            }
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.seriesDeleted'),
            });
          }}
          onUpdateGroup={async (groupId, data) => {
            await adminAvailabilityService.updateGroup(groupId, data);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.seriesUpdated'),
            });
          }}
        />

        {/* Edit Override Dialog - Mobile */}
        <EditOverrideDialog
          open={editOverrideDialogOpen}
          onOpenChange={setEditOverrideDialogOpen}
          override={editOverride}
          services={services
            .filter(s => (s.max_capacity ?? 1) === 1)
            .map(s => ({ id: s.id, name: s.name, active: s.active }))}
          onUpdate={async (id, data) => {
            await adminAvailabilityService.updateOverride(id, data);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditOverrideDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.updated'),
            });
          }}
          onDelete={async (id) => {
            await adminAvailabilityService.deleteOverride(id);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditOverrideDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.deleted'),
            });
          }}
        />

        {/* Group-class slot edit dialog (Details + Participants + Waitlist tabs) */}
        <EditGroupClassSlotDialog
          open={editGroupClassSlotId !== null}
          onOpenChange={(open) => { if (!open) closeGroupClassDialog(); }}
          detail={editGroupClassDetail}
          isLoading={loadingGroupClassDetail}
          onSave={handleSaveGroupClassSlot}
          onCancelClass={handleCancelGroupClassSlot}
          loadProviderOptions={loadGroupClassProviderOptions}
          isSaving={savingGroupClassSlot}
          isCancelling={cancellingGroupClassSlot}
          onViewParticipant={(publicId) => setLocation(`/admin/bookings/${publicId}`)}
          onOpenFullPage={(slotId) => setLocation(`/admin/group-classes/${slotId}`)}
          waitlistEntries={waitlistEntries}
          isLoadingWaitlist={loadingWaitlist}
          onRemoveWaitlistEntry={handleRemoveWaitlistEntry}
        />

        {/* Cancelled-class remove confirmation */}
        <AlertDialog
          open={removeCancelledSlotId !== null}
          onOpenChange={(open) => { if (!open && !removingCancelledSlot) setRemoveCancelledSlotId(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('staff:availability.groupClass.removeConfirm.title', 'This class is cancelled')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  'staff:availability.groupClass.removeConfirm.description',
                  'Removing it deletes the slot from your schedule. Participants were already notified by email.'
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removingCancelledSlot}>
                {t('staff:availability.groupClass.removeConfirm.keep', 'Keep on schedule')}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 text-white hover:bg-rose-700"
                disabled={removingCancelledSlot}
                onClick={(e) => { e.preventDefault(); handleConfirmRemoveCancelled(); }}
              >
                {t('staff:availability.groupClass.removeConfirm.confirm', 'Remove from schedule')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Booking Details Sheet */}
        <Sheet open={bookingSheetOpen} onOpenChange={setBookingSheetOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{t('admin:availability.bookingDetails', 'Booking Details')}</SheetTitle>
              <SheetDescription>
                {selectedBooking?.service_name}
              </SheetDescription>
            </SheetHeader>
            {selectedBooking && (
              <div className="mt-6 space-y-4">
                <div>
                  <div className="text-sm text-soft">{t('common:labels.customer', 'Customer')}</div>
                  <div className="font-medium">{selectedBooking.customer_name}</div>
                  <div className="text-sm text-soft">{selectedBooking.customer_email}</div>
                </div>
                <div>
                  <div className="text-sm text-soft">{t('common:labels.time', 'Time')}</div>
                  <div className="font-medium">
                    {selectedBooking.start_time} - {selectedBooking.end_time}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-soft">{t('common:labels.status', 'Status')}</div>
                  <Badge variant={selectedBooking.status === 'confirmed' ? 'default' : 'secondary'}>
                    {t(`common:status.${selectedBooking.status}`, selectedBooking.status)}
                  </Badge>
                </div>
                {selectedProvider && (
                  <div>
                    <div className="text-sm text-soft">{t('common:labels.provider', 'Provider')}</div>
                    <div className="font-medium">{selectedProvider.display_name}</div>
                  </div>
                )}
                <Button className="w-full mt-4" asChild>
                  <a href={`/admin/bookings/${selectedBooking.public_id}`}>
                    {t('admin:availability.viewFullDetails', 'View Full Details')}
                  </a>
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop: single-provider Calendar (matches MySchedule layout)
  return (
    <div className="space-y-6">
      {/* Provider selector */}
      {allProviders.length > 0 && (
        <div className="bg-white rounded-lg border border-line p-3 overflow-x-auto">
          <div className="flex gap-2 min-w-min">
            {allProviders.map((provider) => {
              const active = provider.id === selectedProviderId;
              return (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => setSelectedProviderId(provider.id)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-md transition-colors whitespace-nowrap',
                    active
                      ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-500'
                      : 'bg-surface-2 text-ink hover:bg-surface-2',
                  )}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={provider.avatar_url || undefined} />
                    <AvatarFallback
                      className={cn('text-[10px]', active ? 'bg-brand-200 text-brand-700' : 'bg-surface-2')}
                    >
                      {getInitials(provider.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{provider.display_name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center text-soft">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <p>{t('common:errors.loadingFailed', 'Failed to load data')}</p>
          </div>
        </div>
      ) : !allProviders.length ? (
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center text-soft">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">{t('admin:availability.noStaff', 'No staff members found')}</p>
            <p className="text-sm">{t('admin:availability.addStaffHint', 'Add team members in the Team page')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-line overflow-hidden">
          <ScheduleCalendar
            view={viewMode}
            date={selectedDate}
            onDateChange={setSelectedDate}
            events={calendarEvents}
            layers={displayLayers}
            loading={isLoading}
            hideViewTabs
            toolbar={
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <TabsList>
                  <TabsTrigger value="day">{t('admin:availability.day', 'Day')}</TabsTrigger>
                  <TabsTrigger value="week">{t('admin:availability.week', 'Week')}</TabsTrigger>
                  <TabsTrigger value="month">{t('admin:availability.month', 'Month')}</TabsTrigger>
                </TabsList>
              </Tabs>
            }
            onEventClick={handleCalendarEventClick}
            onSlotClick={handleCalendarSlotClick}
          />
        </div>
      )}

      {/* Create Availability Dialog */}
      <CreateAvailabilityWizard
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        services={services.map(s => ({
          id: s.id,
          name: s.name,
          active: s.active,
          max_capacity: s.max_capacity,
          duration_minutes: s.duration_minutes ?? s.duration,
        }))}
        providers={gridData?.providers?.map(p => ({
          id: p.id,
          name: p.display_name,
          avatar_url: p.avatar_url,
        })) || []}
        selectedProviderId={createDialogProvider?.id}
        onCreateRecurring={async (data) => {
          const params = {
            provider_id: data.provider_id!,
            days_of_week: data.days_of_week,
            start_time: data.start_time,
            end_time: data.end_time,
            recurrence_type: data.recurrence_type,
            recurrence_interval: data.recurrence_interval,
            monthly_week: data.monthly_week,
            monthly_day: data.monthly_day,
            end_type: data.end_type,
            end_date: data.end_date,
            occurrences: data.occurrences,
            service_ids: data.service_ids,
          };
          try {
            await adminAvailabilityService.createRecurringAvailability(params);

            // Calculate first occurrence date from days_of_week
            const today = new Date();
            const todayDayOfWeek = getDay(today);

            let firstDate = today;
            if (data.days_of_week.length > 0) {
              const sortedDays = [...data.days_of_week].sort((a, b) => a - b);
              const nextDayIndex = sortedDays.find(d => d >= todayDayOfWeek);
              const targetDay = nextDayIndex !== undefined ? nextDayIndex : sortedDays[0];

              if (targetDay === todayDayOfWeek) {
                firstDate = today;
              } else if (targetDay > todayDayOfWeek) {
                firstDate = addDays(today, targetDay - todayDayOfWeek);
              } else {
                firstDate = addDays(today, 7 - todayDayOfWeek + targetDay);
              }
            }

            setViewMode('day');
            setSelectedDate(firstDate);

            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            toast({
              title: t('common:toasts.createSuccess'),
              description: t('staff:availability.created', 'Availability created successfully'),
            });
          } catch (error: any) {
            const errorData = error.response?.data;
            if (errorData?.conflict && errorData?.conflicts) {
              setCreateDialogOpen(false);
              setConflictDialogData({ conflicts: errorData.conflicts, pendingRequest: params });
              return;
            }
            toast({
              title: t('common:toasts.error'),
              description: t('common:errors.createFailed', { resource: 'availability' }),
              variant: 'destructive',
            });
          }
        }}
        onCreateDateRange={async (data) => {
          try {
            await adminAvailabilityService.createDateRangeAvailability({
              provider_id: data.provider_id!,
              start_date: data.start_date,
              end_date: data.end_date,
              start_time: data.start_time,
              end_time: data.end_time,
              reason: data.reason,
              service_ids: data.service_ids,
            });

            const firstDate = parseISO(data.start_date);
            setViewMode('day');
            setSelectedDate(firstDate);

            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            toast({
              title: t('common:toasts.createSuccess'),
              description: t('staff:availability.created', 'Availability created successfully'),
            });
          } catch (error) {
            toast({
              title: t('common:toasts.error'),
              description: t('common:errors.createFailed', { resource: 'availability' }),
              variant: 'destructive',
            });
          }
        }}
        onCreateOneTime={async (data) => {
          try {
            await adminAvailabilityService.createOneTimeAvailability({
              provider_id: data.provider_id!,
              date: data.date,
              start_time: data.start_time,
              end_time: data.end_time,
              reason: data.reason,
              service_ids: data.service_ids,
            });

            const firstDate = parseISO(data.date);
            setViewMode('day');
            setSelectedDate(firstDate);

            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            toast({
              title: t('common:toasts.createSuccess'),
              description: t('staff:availability.created', 'Availability created successfully'),
            });
          } catch (error) {
            toast({
              title: t('common:toasts.error'),
              description: t('common:errors.createFailed', { resource: 'availability' }),
              variant: 'destructive',
            });
          }
        }}
        onCreateGroupRecurring={async (data) => {
          try {
            const svc = services.find(s => s.id === data.service_id);
            const result = await adminAvailabilityService.createGroupClassRecurringAvailability({
              provider_id: data.provider_id!,
              service_id: data.service_id,
              days_of_week: data.days_of_week,
              start_time: data.start_time,
              recurrence_type: data.recurrence_type,
              recurrence_interval: data.recurrence_interval,
              monthly_week: data.monthly_week,
              monthly_day: data.monthly_day,
              end_type: data.end_type,
              end_date: data.end_date,
              occurrences: data.occurrences,
              effective_from: data.effective_from,
            });
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            toast({
              title: t('common:toasts.createSuccess'),
              description: t(
                'staff:availability.groupClass.toasts.created',
                'Created {{count}} {{service}} classes ({{capacity}} spots each)',
                {
                  count: result.slots_created,
                  service: svc?.name ?? 'group',
                  capacity: svc?.max_capacity ?? 0,
                }
              ),
            });
          } catch (error: any) {
            toast({
              title: t('common:toasts.error'),
              description: error?.response?.data?.error
                || t('common:errors.createFailed', { resource: 'availability' }),
              variant: 'destructive',
            });
          }
        }}
        manageServicesHref="/admin/services"
        onCreateGroupOneTime={async (data) => {
          try {
            const svc = services.find(s => s.id === data.service_id);
            await adminAvailabilityService.createGroupClassOneTimeAvailability({
              provider_id: data.provider_id!,
              service_id: data.service_id,
              date: data.date,
              start_time: data.start_time,
            });
            setViewMode('day');
            setSelectedDate(parseISO(data.date));
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            toast({
              title: t('common:toasts.createSuccess'),
              description: t(
                'staff:availability.groupClass.toasts.createdOne',
                'Created 1 {{service}} class ({{capacity}} spots)',
                {
                  service: svc?.name ?? 'group',
                  capacity: svc?.max_capacity ?? 0,
                }
              ),
            });
          } catch (error) {
            toast({
              title: t('common:toasts.error'),
              description: t('common:errors.createFailed', { resource: 'availability' }),
              variant: 'destructive',
            });
          }
        }}
      />

      {/* Edit Availability Dialog */}
      <EditAvailabilityDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        availability={editAvailability}
        selectedDate={editAvailabilityDate || undefined}
        allAvailabilities={weekAvailabilitiesForEdit as ProviderAvailability[]}
        services={services
          .filter(s => editAvailability?.kind === 'group_class'
            ? (s.max_capacity ?? 1) > 1
            : (s.max_capacity ?? 1) === 1)
          .map(s => ({ id: s.id, name: s.name, active: s.active }))}
        onUpdateFuture={async (id, data) => {
          await adminAvailabilityService.updateFutureAvailability(id, data);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.updated'),
          });
        }}
        onCreateMultiDay={async (data) => {
          await staffAvailabilityService.createMultiDayAvailability(data);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
        }}
        onDelete={async (id) => {
          await adminAvailabilityService.deleteAvailability(id);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.deleted'),
          });
        }}
        onDeleteSingleDay={async (date, recurrenceGroupId, startTime, endTime) => {
          if (editProvider) {
            await adminAvailabilityService.markDateUnavailable(editProvider.id, date, undefined, recurrenceGroupId, startTime, endTime);
            queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
            setEditDialogOpen(false);
            toast({
              title: t('common:success'),
              description: t('staff:availability.deleted'),
            });
          }
        }}
        onDeleteGroup={async (groupId, fromDate) => {
          if (fromDate) {
            await adminAvailabilityService.endGroup(groupId, fromDate);
          } else {
            await adminAvailabilityService.deleteGroup(groupId);
          }
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.seriesDeleted'),
          });
        }}
        onUpdateGroup={async (groupId, data) => {
          await adminAvailabilityService.updateGroup(groupId, data);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.seriesUpdated'),
          });
        }}
      />

      {/* Edit Override Dialog */}
      <EditOverrideDialog
        open={editOverrideDialogOpen}
        onOpenChange={setEditOverrideDialogOpen}
        override={editOverride}
        services={services
          .filter(s => (s.max_capacity ?? 1) === 1)
          .map(s => ({ id: s.id, name: s.name, active: s.active }))}
        onUpdate={async (id, data) => {
          await adminAvailabilityService.updateOverride(id, data);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditOverrideDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.updated'),
          });
        }}
        onDelete={async (id) => {
          await adminAvailabilityService.deleteOverride(id);
          queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
          setEditOverrideDialogOpen(false);
          toast({
            title: t('common:success'),
            description: t('staff:availability.deleted'),
          });
        }}
      />

      {/* Group-class slot edit dialog (Details + Participants tabs) */}
      <EditGroupClassSlotDialog
        open={editGroupClassSlotId !== null}
        onOpenChange={(open) => { if (!open) closeGroupClassDialog(); }}
        detail={editGroupClassDetail}
        isLoading={loadingGroupClassDetail}
        onSave={handleSaveGroupClassSlot}
        onCancelClass={handleCancelGroupClassSlot}
        loadProviderOptions={loadGroupClassProviderOptions}
        isSaving={savingGroupClassSlot}
        isCancelling={cancellingGroupClassSlot}
        onViewParticipant={(publicId) => setLocation(`/admin/bookings/${publicId}`)}
        onOpenFullPage={(slotId) => setLocation(`/admin/group-classes/${slotId}`)}
        waitlistEntries={waitlistEntries}
        isLoadingWaitlist={loadingWaitlist}
        onRemoveWaitlistEntry={handleRemoveWaitlistEntry}
      />

      {/* Cancelled-class remove confirmation */}
      <AlertDialog
        open={removeCancelledSlotId !== null}
        onOpenChange={(open) => { if (!open && !removingCancelledSlot) setRemoveCancelledSlotId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('staff:availability.groupClass.removeConfirm.title', 'This class is cancelled')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'staff:availability.groupClass.removeConfirm.description',
                'Removing it deletes the slot from your schedule. Participants were already notified by email.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingCancelledSlot}>
              {t('staff:availability.groupClass.removeConfirm.keep', 'Keep on schedule')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={removingCancelledSlot}
              onClick={(e) => { e.preventDefault(); handleConfirmRemoveCancelled(); }}
            >
              {t('staff:availability.groupClass.removeConfirm.confirm', 'Remove from schedule')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Availability Conflict Dialog */}
      <Dialog open={!!conflictDialogData} onOpenChange={(open) => { if (!open) setConflictDialogData(null); }}>
        <DialogContent className="sm:max-w-[540px] max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('availability.conflicts.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 py-1">
            {/* Current (existing) availability */}
            {conflictDialogData?.conflicts.map((conflict) => (
              <div key={conflict.day_of_week} className="space-y-2">
                {conflict.conflicting.map((item) => (
                  <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1">{t('availability.conflicts.currentLabel')}</p>
                    <p className="text-sm font-medium text-amber-900">
                      {item.services?.map(s => s.name).join(', ') || '—'}{' · '}
                      {item.recurrence_summary ? `${item.recurrence_summary} ${item.start_time} – ${item.end_time}` : `${conflict.day_name} ${item.start_time} – ${item.end_time}`}
                    </p>
                  </div>
                ))}
              </div>
            ))}

            {/* New availability being created */}
            {conflictDialogData && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-1">{t('availability.conflicts.newLabel')}</p>
                <p className="text-sm font-medium text-blue-900">
                  {conflictDialogData.pendingRequest.service_ids?.length
                    ? services.filter(s => conflictDialogData.pendingRequest.service_ids?.includes(s.id)).map(s => s.name).join(', ')
                    : '—'}{' · '}
                  {conflictDialogData.conflicts.map(c => c.day_name).join(', ')}{' · '}
                  {conflictDialogData.pendingRequest.start_time} – {conflictDialogData.pendingRequest.end_time}
                </p>
              </div>
            )}

            {/* Explanation */}
            <p className="text-sm text-soft">
              {t('availability.conflicts.explanation')}
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setConflictDialogData(null);
                setCreateDialogInitialStep(4);
                setCreateDialogOpen(true);
              }}
              className="flex-1"
            >
              {t('availability.conflicts.editAction')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setConflictDialogData(null)}
              className="flex-1"
            >
              {t('availability.conflicts.skipAction')}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!conflictDialogData) return;
                try {
                  await adminAvailabilityService.createRecurringAvailability({
                    ...conflictDialogData.pendingRequest,
                    force_override: true,
                  });
                  setConflictDialogData(null);
                  queryClient.invalidateQueries({ queryKey: ['admin-availability-grid'] });
                  toast({
                    title: t('common:toasts.createSuccess'),
                    description: t('staff:availability.created', 'Availability created successfully'),
                  });
                } catch (error) {
                  toast({
                    title: t('common:toasts.error'),
                    description: t('common:errors.createFailed', { resource: 'availability' }),
                    variant: 'destructive',
                  });
                }
              }}
              className="flex-1"
            >
              {t('availability.conflicts.overrideAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Sheet */}
      <Sheet open={bookingSheetOpen} onOpenChange={setBookingSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('admin:availability.bookingDetails', 'Booking Details')}</SheetTitle>
            <SheetDescription>
              {selectedBooking?.service_name}
            </SheetDescription>
          </SheetHeader>
          {selectedBooking && (
            <div className="mt-6 space-y-4">
              <div>
                <div className="text-sm text-soft">{t('common:labels.customer', 'Customer')}</div>
                <div className="font-medium">{selectedBooking.customer_name}</div>
                <div className="text-sm text-soft">{selectedBooking.customer_email}</div>
              </div>
              <div>
                <div className="text-sm text-soft">{t('common:labels.time', 'Time')}</div>
                <div className="font-medium">
                  {selectedBooking.start_time} - {selectedBooking.end_time}
                </div>
              </div>
              <div>
                <div className="text-sm text-soft">{t('common:labels.status', 'Status')}</div>
                <Badge variant={selectedBooking.status === 'confirmed' ? 'default' : 'secondary'}>
                  {t(`common:status.${selectedBooking.status}`, selectedBooking.status)}
                </Badge>
              </div>
              {selectedProvider && (
                <div>
                  <div className="text-sm text-soft">{t('common:labels.provider', 'Provider')}</div>
                  <div className="font-medium">{selectedProvider.display_name}</div>
                </div>
              )}
              <Button className="w-full mt-4" asChild>
                <a href={`/admin/bookings/${selectedBooking.public_id}`}>
                  {t('admin:availability.viewFullDetails', 'View Full Details')}
                </a>
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

